<template>
    <div class="bg-white border border-gray-200 rounded-lg"
         :class="{ 'ring-2 ring-minplan-darkbeige': ctx.intoKey() === collapseKey }">

        <!-- Kopf -->
        <div class="flex items-center gap-2 pl-4 pr-3 py-3 rounded-t-lg"
             :class="[unassigned ? 'stripe' : '', headerIsInto ? '' : ctx.dropClass(node.uuid)]"
             :style="unassigned ? null : { backgroundColor: ebenenFarbe }"
             @dragover.stop="onHeaderOver" @drop.stop="onHeaderDrop">
            <div class="flex items-center gap-3 flex-1 min-w-0 cursor-pointer select-none"
                 @click="ctx.toggle(collapseKey)">
                <svg class="w-[18px] h-[18px] text-minplan-grey flex-shrink-0 transition-transform"
                     :class="{ '-rotate-90': ctx.isCollapsed(collapseKey) }"
                     viewBox="0 0 24 24" fill="none" stroke="currentColor"><path d="M6 9l6 6 6-6" /></svg>

                <input v-if="!unassigned && ctx.editingUuid() === node.uuid"
                       class="text-[15px] font-semibold text-minplan-blue border border-gray-300 rounded px-2 py-1 min-w-[180px]"
                       :value="node.name" :ref="ctx.setEditRef" @click.stop
                       @keyup.enter="ctx.commitRename(node, $event.target.value)"
                       @keyup.esc="ctx.commitRename(node, node.name)"
                       @blur="ctx.commitRename(node, $event.target.value)" :aria-label="$t('buildings.rename')" />
                <span v-else class="text-[15px] font-semibold"
                      :class="unassigned ? 'text-gray-500' : 'text-minplan-blue'">{{ node.name }}</span>

                <span class="text-xs text-minplan-grey bg-white border border-gray-200 px-2 py-0.5 rounded-full whitespace-nowrap">
                    {{ total }} {{ total === 1 ? $t("buildings.apartment") : $t("buildings.apartments") }}
                </span>
            </div>

            <div v-if="!unassigned" class="relative flex-shrink-0">
                <button type="button" @click.stop="ctx.toggleMenu(node.uuid)"
                        class="w-8 h-8 inline-flex items-center justify-center rounded-lg text-minplan-grey hover:bg-white hover:text-minplan-blue"
                        :aria-label="$t('buildings.menu')" :title="$t('buildings.menu')">
                    <svg viewBox="0 0 24 24" fill="currentColor" class="w-5 h-5">
                        <circle cx="5" cy="12" r="1.6" /><circle cx="12" cy="12" r="1.6" /><circle cx="19" cy="12" r="1.6" />
                    </svg>
                </button>
                <div v-if="ctx.openMenuUuid() === node.uuid"
                     class="absolute right-0 top-9 z-30 min-w-[190px] p-1.5 bg-white border border-gray-200 rounded-lg shadow-lg">
                    <button type="button" class="menu-item" @click.stop="ctx.startRename(node)">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" class="mi"><path d="M12 20h9M16.5 3.5a2.1 2.1 0 013 3L7 19l-4 1 1-4z" /></svg>
                        {{ $t("buildings.rename") }}
                    </button>
                    <!-- Entscheid 2.5: auf der letzten Ebene ist Schluss -->
                    <button type="button" class="menu-item" :disabled="!ctx.canAddSubgroup(level)"
                            :title="ctx.canAddSubgroup(level) ? '' : $t('buildings.depthReached', { max: ctx.maxDepth() })"
                            @click.stop="ctx.startAddUnder(node.uuid)">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" class="mi"><path d="M12 5v14M5 12h14" /></svg>
                        {{ $t("buildings.addNamed", { level: ctx.levelName(level + 1) }) }}
                    </button>
                    <!-- Entscheid 2.1: in ein anderes Gebäude hängen -->
                    <button type="button" class="menu-item" @click.stop="ctx.requestMove('building', node.uuid, node.name)">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" class="mi"><path d="M5 12h14M13 6l6 6-6 6" /></svg>
                        {{ $t("buildings.moveTo") }}
                    </button>
                    <button type="button" class="menu-item" :disabled="ctx.itemPos(parentScope, node.uuid) === 0"
                            @click.stop="ctx.moveGroupStep(parentScope, node.uuid, -1)">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" class="mi"><path d="M12 19V5M6 11l6-6 6 6" /></svg>
                        {{ $t("buildings.moveUp") }}
                    </button>
                    <button type="button" class="menu-item" :disabled="ctx.itemPos(parentScope, node.uuid) === ctx.itemLen(parentScope) - 1"
                            @click.stop="ctx.moveGroupStep(parentScope, node.uuid, 1)">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" class="mi"><path d="M12 5v14M6 13l6 6 6-6" /></svg>
                        {{ $t("buildings.moveDown") }}
                    </button>
                    <button type="button" class="menu-item text-red-600 hover:bg-red-50" @click.stop="ctx.requestDelete(node)">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" class="mi"><path d="M4 7h16M9 7V5a1 1 0 011-1h4a1 1 0 011 1v2M7 7l1 12a1 1 0 001 1h6a1 1 0 001-1l1-12" /></svg>
                        {{ $t("buildings.delete") }}
                    </button>
                </div>
            </div>
        </div>

        <!-- Körper: gemischte, sortierte Elemente (Wohnungen + Untergruppen) -->
        <div v-show="!ctx.isCollapsed(collapseKey)" class="p-3 flex flex-col gap-2 rounded-b-lg"
             :style="{ backgroundColor: ebenenFarbe }"
             @dragover.stop="ctx.onIntoOver(ownScope, $event)" @drop.stop="ctx.onIntoDrop(ownScope, $event)">
            <template v-for="item in renderItems" :key="item.uuid">
                <div v-if="item.type === 'apartment' && item.apt"
                     class="group flex items-stretch gap-1.5 rounded-lg transition-opacity"
                     :class="[ctx.dropClass(item.uuid), { 'opacity-30': ctx.draggingUuid() === item.uuid }]"
                     @dragover.stop="ctx.onItemOver(ownScope, item.uuid, $event)"
                     @drop.stop="ctx.onItemDrop(ownScope, item.uuid, $event)">
                    <span class="flex items-center justify-center w-7 flex-shrink-0 text-gray-400 group-hover:text-minplan-blue cursor-grab text-xl leading-none transition-colors"
                          draggable="true" :title="$t('buildings.dragToMove')"
                          @dragstart="ctx.onDragStart(item.apt, $event)" @dragend="ctx.onDragEnd">⠿</span>
                    <div class="flex-1 min-w-0">
                        <projects-apartments-card :apartment="item.apt" :project="ctx.project()" />
                    </div>
                    <!-- Entscheid 2.3: derselbe Dialog auch für Wohnungen — damit die
                         Zuordnung ohne Maus und auf Touch-Geräten möglich ist. -->
                    <div class="relative flex items-center flex-shrink-0">
                        <button type="button" @click.stop="ctx.toggleMenu(item.uuid)"
                                class="w-8 h-8 inline-flex items-center justify-center rounded-lg text-gray-400 hover:bg-white hover:text-minplan-blue"
                                :aria-label="$t('buildings.menu')" :title="$t('buildings.menu')">
                            <svg viewBox="0 0 24 24" fill="currentColor" class="w-5 h-5">
                                <circle cx="5" cy="12" r="1.6" /><circle cx="12" cy="12" r="1.6" /><circle cx="19" cy="12" r="1.6" />
                            </svg>
                        </button>
                        <div v-if="ctx.openMenuUuid() === item.uuid"
                             class="absolute right-0 top-9 z-30 min-w-[190px] p-1.5 bg-white border border-gray-200 rounded-lg shadow-lg">
                            <button type="button" class="menu-item"
                                    @click.stop="ctx.requestMove('apartment', item.uuid, item.apt.name)">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" class="mi"><path d="M5 12h14M13 6l6 6-6 6" /></svg>
                                {{ $t("buildings.moveTo") }}
                            </button>
                        </div>
                    </div>
                </div>

                <projects-building-group v-else-if="item.type === 'building' && item.child"
                    :node="item.child" :parent-scope="ownScope" :level="level + 1" />
            </template>

            <div v-if="!unassigned && ctx.addingParentUuid() === node.uuid"
                 class="flex items-center px-3.5 py-3 border border-dashed border-gray-300 rounded-lg">
                <input class="w-full text-sm text-minplan-blue border border-gray-300 rounded px-2.5 py-2"
                       :placeholder="$t('buildings.nameOf', { level: ctx.levelName(level + 1) })" :ref="ctx.setAddRef"
                       @keyup.enter="ctx.commitAdd($event.target.value)" @keyup.esc="ctx.cancelAdd"
                       @blur="ctx.commitAdd($event.target.value)" />
            </div>

            <div v-if="!renderItems.length && ctx.addingParentUuid() !== node.uuid"
                 class="px-1 py-3 text-minplan-grey text-sm italic">
                {{ $t("buildings.emptyHint") }}
            </div>
        </div>
    </div>
</template>

<script>
import ProjectsApartmentsCard from "@/Shared/Components/Projects/ProjectsApartmentsCard";

// Thema 16/17 — rekursive Gebäude-Gruppe mit freier Anordnung. Wohnungen und
// Untergruppen einer Ebene teilen sich eine Reihenfolge (ctx.itemsOf). Geteilter
// Zustand kommt via inject (groupCtx) aus ProjectsApartments.
// Drag-and-drop ist natives HTML5-DnD (Maus/Desktop). Auf Touch übernimmt
// „Verschieben nach…" im ⋯-Menü (Entscheid 2.3) — für Gruppen und Wohnungen.
export default {
    name: "ProjectsBuildingGroup",
    components: { ProjectsApartmentsCard },
    inject: ["groupCtx"],
    props: {
        node: { type: Object, required: true },
        parentScope: { default: null }, // '__root__' | <uuid> | null (unassigned)
        // 1-basiert wie überall sonst: Wurzel-Gebäude sind Ebene 1.
        level: { type: Number, default: 1 },
        unassigned: { type: Boolean, default: false },
    },
    computed: {
        /**
         * Farbliche Staffelung der Verschachtelung (Entscheid Peter,
         * 31.08.2026): EINE Fläche je Ebene, nach innen heller.
         *
         * Kopf und Körper tragen bewusst DENSELBEN Ton — eine Ebene ist damit
         * ein einziger zusammenhängender Bereich, und man sieht ohne
         * Nachdenken, was worin liegt. Die Titelzeile hebt sich durch die
         * fettere Schrift ab, dafür braucht es keinen zweiten Farbton.
         *
         * Erster Versuch war ein Kopf, der eine Spur dunkler war als sein
         * eigener Körper. Das ergab zwei Unterscheidungen übereinander (Kopf
         * gegen Körper UND Ebene gegen Ebene) und liess das Kind an seiner
         * Oberkante dunkler erscheinen als die Fläche, in der es liegt —
         * genau verkehrt, die Schachtelung franste aus.
         *
         * Vier Stufen, weil `Building::MAX_DEPTH` vier ist; tiefere Ebenen
         * behalten die letzte. Die unterste Stufe bleibt bewusst von Weiss
         * unterscheidbar: die WOHNUNGEN sind weiss und sollen auf jeder Ebene
         * hervorstechen.
         */
        ebenenFarbe() {
            const stufen = ["#E9E1D6", "#EFE9E1", "#F5F1EB", "#FAF8F5"];

            return stufen[Math.min(this.level, stufen.length) - 1];
        },
        ctx() {
            return this.groupCtx;
        },
        collapseKey() {
            return this.unassigned ? "__unassigned__" : this.node.uuid;
        },
        ownScope() {
            return this.unassigned ? null : this.node.uuid;
        },
        headerIsInto() {
            return this.unassigned || this.parentScope === "__root__";
        },
        total() {
            return this.ctx.totalCount(this.node);
        },
        renderItems() {
            return this.ctx.itemsOf(this.ownScope).map((it) => it.type === "apartment"
                ? { type: "apartment", uuid: it.uuid, apt: this.ctx.apartmentByUuid(it.uuid) }
                : { type: "building", uuid: it.uuid, child: (this.node.children || []).find((c) => c.uuid === it.uuid) });
        },
    },
    methods: {
        onHeaderOver(event) {
            if (this.headerIsInto) this.ctx.onIntoOver(this.ownScope, event);
            else this.ctx.onItemOver(this.parentScope, this.node.uuid, event);
        },
        onHeaderDrop(event) {
            if (this.headerIsInto) this.ctx.onIntoDrop(this.ownScope, event);
            else this.ctx.onItemDrop(this.parentScope, this.node.uuid, event);
        },
    },
};
</script>

<style scoped>
.menu-item { display: flex; align-items: center; gap: 10px; width: 100%; padding: 8px 10px; border-radius: 6px; font-size: 14px; color: #2f3441; text-align: left; }
.menu-item:hover { background: #f6f2ee; }
.menu-item:disabled { opacity: .4; }
.mi { width: 16px; height: 16px; stroke-width: 2; flex: 0 0 auto; }
.stripe { background: repeating-linear-gradient(135deg, #f6f2ee, #f6f2ee 10px, #f2ece5 10px, #f2ece5 20px); }
/* Einfügelinie beim Drag-and-drop: klare Linie mit Punkt genau in der Lücke,
   wo die Wohnung landet — statt eines Rahmens, der wie eine Auswahl wirkt.
   Farbe: minplan-lightgrey (#6f7c90, „Blaugrau") als interaktiver Marker. */
.drop-top, .drop-bot { position: relative; }
.drop-top::before, .drop-bot::before {
    content: ""; position: absolute; left: 0; right: 0; height: 2px;
    background: #6f7c90; border-radius: 2px; z-index: 5; pointer-events: none;
}
.drop-top::after, .drop-bot::after {
    content: ""; position: absolute; left: -1px; width: 8px; height: 8px;
    border-radius: 50%; background: #6f7c90; z-index: 5; pointer-events: none;
}
.drop-top::before { top: -5px; }
.drop-top::after  { top: -8px; }
.drop-bot::before { bottom: -5px; }
.drop-bot::after  { bottom: -8px; }
</style>
